// Starlyzed Big Three module — sections/starlyzed-big-three.liquid.
//
// A visitor enters birth date, time (unknown allowed) and place; the storefront asks OUR engine for
// the Sun, Moon and Rising signs through the same-origin chart-preview app proxy (two Shopify-signed
// hops, single-use capability — see apps/chart-preview/README.md, "The Big Three data hop"), shows
// them honestly (no Rising without a birth time; both Moon signs when the Moon changed sign that
// day), and only then offers an unchecked consent box to receive sign-personalised email.
//
// PRIVACY CONTRACT (kickoff, 2026-09-16). Birth data is personal data. It lives in this module's
// memory for the life of the page and nowhere else: no localStorage, no sessionStorage, no cookie,
// no URL, no analytics event. The ONLY thing that leaves the browser toward the lead endpoint is
// the envelope `buildBigThreeEnvelope` returns — email, optional first name, the three sign results
// and their honesty flags, consent record, source and campaign attribution. It never carries a
// date, time, place or coordinate; scripts/tests/theme-big-three.test.mjs asserts the key set.
//
// DRY-RUN CONTRACT. The lead endpoint is a section setting. While it is blank the poster logs the
// exact envelope to the console, performs ZERO network calls, resolves as `mode: "dry-run"`, and
// the UI says "Test mode: nothing was sent" instead of pretending. The contract the endpoint must
// implement is docs/marketing/big-three-module-2026-09-16.md.
//
// Every DOM-free function below is exported so the contract can be proved in node without a
// browser; the custom element at the bottom is the only thing that touches `document`.

export const BIG_THREE_CONTRACT_VERSION = 1;
export const BIG_THREE_SOURCE = 'starlyzed-theme/big-three-module';
export const BIG_THREE_CONSENT_VERSION = 'starlyzed-big-three-v1';
export const BIG_THREE_SUBMIT_PATH = '/api/big-three';
export const BIG_THREE_TEMPLATE = 'big-three';
export const DEFAULT_PROXY_BASE = '/apps/chart-preview';
export const DEFAULT_TIMEOUT_MS = 10000;
export const CAPABILITY_HEADER = 'X-Preview-Capability';
const PHOTON_ENDPOINT = 'https://photon.komoot.io/api/';
const PLACE_FETCH_LIMIT = 30;
const PLACE_MAX_SUGGESTIONS = 8;
const PLAIN_PLACE_TYPES = new Set(['city', 'town', 'village', 'hamlet']);
const POPULATED_PLACE_TYPES = new Set(['city', 'town', 'village', 'hamlet', 'suburb', 'quarter', 'neighbourhood', 'borough', 'locality']);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}$/;
const CAMPAIGN_VALUE_RE = /^[A-Za-z0-9._~-]{1,160}$/;

export const SIGNS = Object.freeze([
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
]);
const GLYPHS = Object.freeze({
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋', Leo: '♌', Virgo: '♍',
  Libra: '♎', Scorpio: '♏', Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
});

/** Keys that describe a birth, in every spelling this codebase has used. None may enter the envelope. */
export const BIRTH_DATA_KEYS = Object.freeze([
  'date', 'time', 'place', 'lat', 'lon', 'lng', 'tz', 'timezone', 'fold',
  'birth_date', 'birth_time', 'birth_place', 'birthDate', 'birthTime', 'birthPlace',
  'dateLocal', 'timeLocal', 'coordinates', 'chart', 'chartInputs', 'birth_month_day', 'birthMonthDay',
]);

/** The campaign parameters the growthos redirector forwards onto the destination (registry.mjs). */
export const ATTRIBUTION_KEYS = Object.freeze(['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref', 'source']);

export class BigThreeError extends Error {
  /** @param {'input'|'capability'|'engine'|'malformed'|'network'|'timeout'|'submit'} kind */
  constructor(kind, message, code = '') {
    super(message);
    this.name = 'BigThreeError';
    this.kind = kind;
    this.code = code;
  }
}

function isSign(value) {
  return typeof value === 'string' && SIGNS.includes(value);
}

// --- input ----------------------------------------------------------------------------------------

/**
 * Validate the form state into the exact body the Worker's big-three hop accepts. Coordinates come
 * from the place picker as strings; an unselected place has none and is refused here, before any
 * network call is made.
 *
 * @returns {{ ok: true, chart: { template: string, date: string, time: string, timeKnown: boolean, lat: number, lon: number } } | { ok: false, error: BigThreeError }}
 */
export function normalizeBirthInput({ date, time, timeUnknown, lat, lon } = {}) {
  const dateText = String(date || '').trim();
  const timeText = String(time || '').trim();
  const latText = String(lat ?? '').trim();
  const lonText = String(lon ?? '').trim();
  const latNumber = Number(latText);
  const lonNumber = Number(lonText);
  const refuse = (message) => ({ ok: false, error: new BigThreeError('input', message) });

  if (!DATE_RE.test(dateText)) return refuse('Add your birth date.');
  const timeKnown = timeUnknown !== true && timeText !== '';
  if (!timeKnown && timeUnknown !== true) return refuse('Add your birth time, or tick "I don’t know my birth time".');
  if (timeKnown && !TIME_RE.test(timeText)) return refuse('Use a clock time like 09:40.');
  if (!latText || !lonText || !Number.isFinite(latNumber) || !Number.isFinite(lonNumber)
    || Math.abs(latNumber) > 90 || Math.abs(lonNumber) > 180) {
    return refuse('Choose your birthplace from the list so we have its coordinates.');
  }
  return {
    ok: true,
    chart: {
      template: BIG_THREE_TEMPLATE,
      date: dateText,
      time: timeKnown ? timeText : '',
      timeKnown,
      lat: latNumber,
      lon: lonNumber,
    },
  };
}

// --- engine call (two same-origin proxy hops) -----------------------------------------------------

function sameOriginBase(base) {
  const raw = String(base || DEFAULT_PROXY_BASE).trim() || DEFAULT_PROXY_BASE;
  if (typeof location === 'undefined' || !location.origin) {
    if (/^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(raw)) throw new BigThreeError('input', 'The chart service is unavailable right now.');
    return raw.replace(/\/+$/, '') || DEFAULT_PROXY_BASE;
  }
  let url;
  try {
    url = new URL(raw, location.origin);
  } catch {
    throw new BigThreeError('input', 'The chart service is unavailable right now.');
  }
  if (url.origin !== location.origin) throw new BigThreeError('input', 'The chart service is unavailable right now.');
  return url.pathname.replace(/\/+$/, '') || DEFAULT_PROXY_BASE;
}

async function fetchWithTimeout(fetchImpl, url, init, { timeoutMs, setTimeoutImpl, clearTimeoutImpl, AbortControllerImpl }) {
  const controller = typeof AbortControllerImpl === 'function' ? new AbortControllerImpl() : null;
  let timer = null;
  let timedOut = false;
  const request = Promise.resolve().then(() => fetchImpl(url, controller ? { ...init, signal: controller.signal } : init));
  const timeout = new Promise((_, reject) => {
    timer = setTimeoutImpl(() => {
      timedOut = true;
      try {
        controller?.abort();
      } catch {
        // a non-standard controller must not prevent the bounded rejection
      }
      reject(new BigThreeError('timeout', 'That took too long. Please try again.'));
    }, Number.isFinite(timeoutMs) && timeoutMs > 0 ? timeoutMs : DEFAULT_TIMEOUT_MS);
  });
  try {
    return await Promise.race([request, timeout]);
  } catch (error) {
    if (timedOut || error?.kind === 'timeout') throw new BigThreeError('timeout', 'That took too long. Please try again.');
    throw error;
  } finally {
    if (timer !== null) clearTimeoutImpl(timer);
  }
}

async function readJson(response) {
  try {
    return { parsed: true, value: await response.json() };
  } catch {
    return { parsed: false, value: null };
  }
}

/** Engine civil-time codes, said in a way a visitor can act on. Anything else keeps the Worker's message. */
export function engineErrorMessage(code, fallback) {
  if (code === 'ambiguous_civil_time') {
    return 'That clock time happened twice on your birthday (the clocks went back). Try one hour earlier or later, or tick "I don’t know my birth time".';
  }
  if (code === 'nonexistent_civil_time') {
    return 'That clock time did not exist where you were born (the clocks skipped it). Check the time, or tick "I don’t know my birth time".';
  }
  if (code === 'template_not_permitted') return 'The Big Three service is not switched on yet.';
  if (code === 'rate_limited') return 'A lot of people are reading their sky right now. Try again in a minute.';
  return fallback;
}

/**
 * Mint a single-use capability for `big-three`, spend it on one data call, return ONLY the signs.
 *
 * @param {{ base?: string, chart: object, fetchImpl?: typeof fetch, timeoutMs?: number, setTimeoutImpl?: typeof setTimeout, clearTimeoutImpl?: typeof clearTimeout, AbortControllerImpl?: typeof AbortController }} options
 * @returns {Promise<{ sun: string, moon: string, moonCandidates: string[], rising: string|null, timeKnown: boolean, chartMathVersion: string|null }>}
 */
export async function requestBigThree({
  base = DEFAULT_PROXY_BASE,
  chart,
  fetchImpl = fetch,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  setTimeoutImpl = setTimeout,
  clearTimeoutImpl = clearTimeout,
  AbortControllerImpl = typeof AbortController === 'function' ? AbortController : null,
}) {
  const resolvedBase = sameOriginBase(base);
  const timing = { timeoutMs, setTimeoutImpl, clearTimeoutImpl, AbortControllerImpl };
  if (!chart || chart.template !== BIG_THREE_TEMPLATE) throw new BigThreeError('input', 'Add your birth details first.');

  let capabilityResponse;
  try {
    capabilityResponse = await fetchWithTimeout(
      fetchImpl,
      `${resolvedBase}/capability?template=${encodeURIComponent(BIG_THREE_TEMPLATE)}`,
      { method: 'POST', headers: { Accept: 'application/json' }, body: '' },
      timing,
    );
  } catch (error) {
    if (error?.kind === 'timeout') throw error;
    throw new BigThreeError('network', 'The chart service is unavailable right now. Please try again.');
  }
  const capabilityResult = await readJson(capabilityResponse);
  const capabilityBody = capabilityResult.value;
  if (!capabilityResponse.ok) {
    throw new BigThreeError('capability', engineErrorMessage(capabilityBody?.error, 'We could not start your reading. Please try again.'), capabilityBody?.error || '');
  }
  const capability = typeof capabilityBody?.capability === 'string' ? capabilityBody.capability.trim() : '';
  if (!capabilityResult.parsed || !capability || (capabilityBody.template && capabilityBody.template !== BIG_THREE_TEMPLATE)) {
    throw new BigThreeError('malformed', 'We could not start your reading. Please try again.');
  }

  let response;
  try {
    response = await fetchWithTimeout(
      fetchImpl,
      `${resolvedBase}/${BIG_THREE_TEMPLATE}`,
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', [CAPABILITY_HEADER]: capability },
        body: JSON.stringify({
          template: BIG_THREE_TEMPLATE,
          date: chart.date,
          time: chart.timeKnown ? chart.time : '',
          timeKnown: chart.timeKnown === true,
          lat: chart.lat,
          lon: chart.lon,
        }),
      },
      timing,
    );
  } catch (error) {
    if (error?.kind === 'timeout') throw error;
    throw new BigThreeError('network', 'The chart service is unavailable right now. Please try again.');
  }
  const result = await readJson(response);
  const body = result.value;
  if (!response.ok) {
    throw new BigThreeError('engine', engineErrorMessage(body?.error, body?.message || 'We could not compute that chart. Please try again.'), body?.error || '');
  }
  if (!result.parsed || !body || typeof body !== 'object' || !isSign(body.sun) || !isSign(body.moon)) {
    throw new BigThreeError('malformed', 'The chart came back incomplete. Please try again.');
  }
  const timeKnown = body.timeKnown === true;
  return {
    sun: body.sun,
    moon: body.moon,
    moonCandidates: Array.isArray(body.moonCandidates) ? body.moonCandidates.filter(isSign) : [],
    // Never show a Rising the engine did not compute from a known time.
    rising: timeKnown && isSign(body.rising) ? body.rising : null,
    timeKnown,
    chartMathVersion: typeof body.chartMathVersion === 'string' ? body.chartMathVersion : null,
  };
}

// --- honest presentation ---------------------------------------------------------------------------

/**
 * The words the module shows for a result. Rising without a birth time is stated as missing, never
 * guessed; an untimed Moon that changed sign is shown as both candidates.
 */
export function describeBigThree(result) {
  const moonCandidates = Array.isArray(result?.moonCandidates) ? result.moonCandidates.filter(isSign) : [];
  const moonSplit = moonCandidates.length > 1;
  const rising = result?.timeKnown === true && isSign(result.rising) ? result.rising : null;
  return {
    sun: {
      label: 'Sun',
      sign: result.sun,
      glyph: GLYPHS[result.sun] || '',
      line: 'The core of you. Where your light comes from.',
    },
    moon: {
      label: 'Moon',
      sign: moonSplit ? moonCandidates.join(' or ') : result.moon,
      glyph: moonSplit ? moonCandidates.map((s) => GLYPHS[s] || '').join(' ') : (GLYPHS[result.moon] || ''),
      line: 'Your inner weather. What you need to feel at home.',
      note: moonSplit
        ? `The Moon changed signs on your birthday — ${moonCandidates.join(' or ')}. A birth time settles it.`
        : '',
    },
    rising: {
      label: 'Rising',
      sign: rising,
      glyph: rising ? GLYPHS[rising] || '' : '',
      line: rising ? 'How you arrive. The first impression you leave.' : '',
      note: rising
        ? ''
        : 'Rising needs a birth time. We won’t guess it — add the time above if you find it, and we’ll complete your three.',
    },
    complete: Boolean(rising) && !moonSplit,
  };
}

// --- consent-gated lead envelope -------------------------------------------------------------------

/**
 * Campaign attribution the redirector forwards onto the landing page. Allowlisted keys, allowlisted
 * characters, or nothing — never the whole query string.
 */
export function readAttribution(search) {
  const params = new URLSearchParams(String(search || ''));
  const attribution = {};
  for (const key of ATTRIBUTION_KEYS) {
    const value = params.get(key);
    if (typeof value === 'string' && CAMPAIGN_VALUE_RE.test(value)) attribution[key] = value;
  }
  return attribution;
}

/**
 * The exact JSON that goes to `<endpoint>/api/big-three`. Returns null unless consent is explicit
 * and the email is well-formed. Sign results are copied field by field from the engine answer; the
 * birth input is not an argument to this function, which is how it stays out.
 *
 * @param {{ email: string, firstName?: string, consent: boolean, result: object, attribution?: object, pagePath?: string, now?: () => Date }} args
 */
export function buildBigThreeEnvelope({ email, firstName, consent, result, attribution = {}, pagePath = '', now = () => new Date() } = {}) {
  if (consent !== true) return null;
  const address = typeof email === 'string' ? email.trim() : '';
  if (!EMAIL_RE.test(address) || address.length > 254) return null;
  if (!result || !isSign(result.sun) || !isSign(result.moon)) return null;

  const moonCandidates = Array.isArray(result.moonCandidates) ? result.moonCandidates.filter(isSign) : [];
  const timeKnown = result.timeKnown === true;
  const rising = timeKnown && isSign(result.rising) ? result.rising : null;
  const cleanAttribution = {};
  for (const key of ATTRIBUTION_KEYS) {
    const value = attribution?.[key];
    if (typeof value === 'string' && CAMPAIGN_VALUE_RE.test(value)) cleanAttribution[key] = value;
  }
  const name = typeof firstName === 'string' ? firstName.trim().slice(0, 80) : '';

  const envelope = {
    schema_version: BIG_THREE_CONTRACT_VERSION,
    source: BIG_THREE_SOURCE,
    email: address,
    consent: true,
    consent_version: BIG_THREE_CONSENT_VERSION,
    consented_at: now().toISOString(),
    sun_sign: result.sun,
    moon_sign: result.moon,
    moon_sign_candidates: moonCandidates.length > 1 ? moonCandidates : [],
    rising_sign: rising,
    time_known: timeKnown,
    chart_math_version: typeof result.chartMathVersion === 'string' ? result.chartMathVersion : null,
    page_path: typeof pagePath === 'string' ? pagePath.slice(0, 200) : '',
    attribution: cleanAttribution,
  };
  if (name) envelope.first_name = name;
  return envelope;
}

/** The endpoint is a section setting; blank means dry-run. Anything set must be an absolute https origin. */
export function resolveSubmitEndpoint(endpoint) {
  const raw = String(endpoint || '').trim();
  if (!raw) return { mode: 'dry-run', url: null };
  let url;
  try {
    url = new URL(raw);
  } catch {
    return { mode: 'invalid', url: null };
  }
  if (url.protocol !== 'https:' || url.search || url.hash) return { mode: 'invalid', url: null };
  const base = `${url.origin}${url.pathname.replace(/\/+$/, '')}`;
  return { mode: 'live', url: base.endsWith(BIG_THREE_SUBMIT_PATH) ? base : `${base}${BIG_THREE_SUBMIT_PATH}` };
}

/**
 * Deliver the envelope. Dry-run: log it, touch no network, say so. Live: one POST, bounded.
 *
 * @returns {Promise<{ ok: boolean, mode: 'dry-run'|'live', status?: number, error?: string }>}
 */
export async function postBigThree({
  endpoint,
  envelope,
  fetchImpl = fetch,
  logImpl = (...args) => console.info(...args),
  timeoutMs = DEFAULT_TIMEOUT_MS,
  setTimeoutImpl = setTimeout,
  clearTimeoutImpl = clearTimeout,
  AbortControllerImpl = typeof AbortController === 'function' ? AbortController : null,
} = {}) {
  if (!envelope || envelope.consent !== true) throw new BigThreeError('submit', 'Tick the box if you would like your Big Three by email.');
  for (const key of BIRTH_DATA_KEYS) {
    if (key in envelope) throw new BigThreeError('submit', 'Refusing to send birth data.');
  }
  const target = resolveSubmitEndpoint(endpoint);
  if (target.mode === 'invalid') throw new BigThreeError('submit', 'This form is not connected correctly yet.');
  if (target.mode === 'dry-run') {
    logImpl(`[starlyzed big-three dry-run] would POST ${BIG_THREE_SUBMIT_PATH}`, JSON.stringify(envelope));
    return { ok: true, mode: 'dry-run' };
  }

  let response;
  try {
    response = await fetchWithTimeout(
      fetchImpl,
      target.url,
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(envelope),
        credentials: 'omit',
        mode: 'cors',
      },
      { timeoutMs, setTimeoutImpl, clearTimeoutImpl, AbortControllerImpl },
    );
  } catch (error) {
    if (error?.kind === 'timeout') throw error;
    throw new BigThreeError('network', 'We could not reach the mailing list right now. Please try again.');
  }
  const result = await readJson(response);
  if (!response.ok) {
    const code = typeof result.value?.error === 'string' ? result.value.error : `http_${response.status}`;
    return { ok: false, mode: 'live', status: response.status, error: code };
  }
  return { ok: true, mode: 'live', status: response.status };
}

// --- place search (Photon, same disambiguation as the quiz) ----------------------------------------

export function placeLabel(feature, { county = false } = {}) {
  const props = feature?.properties || {};
  const name = props.name || props.city || '';
  const type = String(props.osm_value || '').replace(/_/g, ' ');
  const typed = name && type && !PLAIN_PLACE_TYPES.has(type) ? `${name} (${type})` : name;
  return [typed, county ? props.county : '', props.state, props.country].filter(Boolean).join(', ');
}

/** Populated places first, colliding labels gain their county, then dedupe — so two same-named towns never fold into one row. */
export function rankPlaces(features) {
  const list = Array.isArray(features) ? features : [];
  const isPopulated = (f) => POPULATED_PLACE_TYPES.has(String((f.properties || {}).osm_value || '').replace(/_/g, ' '));
  const ordered = [...list.filter(isPopulated), ...list.filter((f) => !isPopulated(f))];
  let places = ordered.map((feature) => ({ feature, label: placeLabel(feature) }));
  const counts = new Map();
  for (const item of places) counts.set(item.label, (counts.get(item.label) || 0) + 1);
  places = places.map((item) => (counts.get(item.label) > 1 ? { feature: item.feature, label: placeLabel(item.feature, { county: true }) } : item));
  const seen = new Set();
  return places.filter((item) => {
    if (!item.label || seen.has(item.label)) return false;
    seen.add(item.label);
    return true;
  }).slice(0, PLACE_MAX_SUGGESTIONS).map((item) => {
    const coords = item.feature?.geometry?.coordinates || [];
    return { label: item.label, lon: coords[0] != null ? String(coords[0]) : '', lat: coords[1] != null ? String(coords[1]) : '' };
  });
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
}

function debounce(fn, wait) {
  let timer = null;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

// --- the element -----------------------------------------------------------------------------------

if (typeof customElements !== 'undefined' && !customElements.get('starlyzed-big-three')) {
  class StarlyzedBigThree extends HTMLElement {
    connectedCallback() {
      // In-memory only. Nothing in this object is ever written to storage or the URL.
      this.state = { place: '', lat: '', lon: '', result: null, busy: false };
      this.proxyBase = this.dataset.proxyBase || DEFAULT_PROXY_BASE;
      this.submitEndpoint = this.dataset.submitEndpoint || '';
      this.$ = (selector) => this.querySelector(selector);

      const dateInput = this.$('[data-b3-date]');
      if (dateInput && !dateInput.max) dateInput.max = new Date().toISOString().slice(0, 10);

      this.$('[data-b3-time-unknown]')?.addEventListener('change', (event) => {
        const timeInput = this.$('[data-b3-time]');
        if (!timeInput) return;
        timeInput.disabled = event.target.checked;
        if (event.target.checked) timeInput.value = '';
      });

      const placeInput = this.$('[data-b3-place]');
      const suggestions = this.$('[data-b3-suggestions]');
      if (placeInput) {
        placeInput.addEventListener('input', debounce(() => this.searchPlace(placeInput, suggestions), 250));
        placeInput.addEventListener('blur', () => setTimeout(() => this.closeSuggestions(suggestions), 150));
      }

      this.$('[data-b3-form]')?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.reveal();
      });
      this.$('[data-b3-email-form]')?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitEmail();
      });
    }

    setStatus(selector, message, { error = false } = {}) {
      const node = this.$(selector);
      if (!node) return;
      node.textContent = message || '';
      node.hidden = !message;
      node.classList.toggle('stz-b3__status--error', Boolean(error));
    }

    closeSuggestions(list) {
      if (!list) return;
      list.hidden = true;
      list.innerHTML = '';
    }

    async searchPlace(input, list) {
      this.state.place = '';
      this.state.lat = '';
      this.state.lon = '';
      const query = input.value.trim();
      if (!list || query.length < 3) {
        this.closeSuggestions(list);
        return;
      }
      try {
        const response = await fetch(`${PHOTON_ENDPOINT}?q=${encodeURIComponent(query)}&limit=${PLACE_FETCH_LIMIT}&lang=en&osm_tag=place`);
        if (!response.ok) return;
        const data = await response.json();
        const places = rankPlaces(data.features);
        list.innerHTML = places.map((item, index) => `<li><button type="button" data-b3-place-index="${index}">${escapeHtml(item.label)}</button></li>`).join('');
        list.hidden = places.length === 0;
        list.querySelectorAll('[data-b3-place-index]').forEach((button) => {
          button.addEventListener('mousedown', (event) => {
            event.preventDefault();
            const item = places[Number(button.getAttribute('data-b3-place-index'))];
            input.value = item.label;
            this.state.place = item.label;
            this.state.lat = item.lat;
            this.state.lon = item.lon;
            this.closeSuggestions(list);
          });
        });
      } catch {
        this.closeSuggestions(list);
      }
    }

    async reveal() {
      if (this.state.busy) return;
      const normalized = normalizeBirthInput({
        date: this.$('[data-b3-date]')?.value,
        time: this.$('[data-b3-time]')?.value,
        timeUnknown: Boolean(this.$('[data-b3-time-unknown]')?.checked),
        lat: this.state.lat,
        lon: this.state.lon,
      });
      if (!normalized.ok) {
        this.setStatus('[data-b3-form-status]', normalized.error.message, { error: true });
        return;
      }
      this.state.busy = true;
      const button = this.$('[data-b3-reveal]');
      if (button) button.disabled = true;
      this.setStatus('[data-b3-form-status]', 'Reading your sky…');
      try {
        const result = await requestBigThree({ base: this.proxyBase, chart: normalized.chart });
        this.state.result = result;
        this.renderResult(result);
        this.setStatus('[data-b3-form-status]', '');
      } catch (error) {
        this.setStatus('[data-b3-form-status]', error?.message || 'Something went wrong. Please try again.', { error: true });
      } finally {
        this.state.busy = false;
        if (button) button.disabled = false;
      }
    }

    renderResult(result) {
      const copy = describeBigThree(result);
      const panel = this.$('[data-b3-result]');
      if (!panel) return;
      for (const key of ['sun', 'moon', 'rising']) {
        const part = copy[key];
        const glyph = this.$(`[data-b3-${key}-glyph]`);
        const sign = this.$(`[data-b3-${key}-sign]`);
        const line = this.$(`[data-b3-${key}-line]`);
        const note = this.$(`[data-b3-${key}-note]`);
        if (glyph) glyph.textContent = part.glyph;
        if (sign) sign.textContent = part.sign || '—';
        if (line) {
          line.textContent = part.line;
          line.hidden = !part.line;
        }
        if (note) {
          note.textContent = part.note || '';
          note.hidden = !part.note;
        }
      }
      panel.hidden = false;
      const emailPanel = this.$('[data-b3-email]');
      if (emailPanel) emailPanel.hidden = false;
      this.setStatus('[data-b3-email-status]', '');
      this.$('[data-b3-success]')?.setAttribute('hidden', '');
      panel.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    }

    async submitEmail() {
      if (this.state.busy || !this.state.result) return;
      const consent = Boolean(this.$('[data-b3-consent]')?.checked);
      const email = this.$('[data-b3-email-input]')?.value || '';
      if (!consent) {
        this.setStatus('[data-b3-email-status]', 'Tick the box if you would like your Big Three by email.', { error: true });
        return;
      }
      const envelope = buildBigThreeEnvelope({
        email,
        firstName: this.$('[data-b3-first-name]')?.value || '',
        consent,
        result: this.state.result,
        attribution: readAttribution(typeof location !== 'undefined' ? location.search : ''),
        pagePath: typeof location !== 'undefined' ? location.pathname : '',
      });
      if (!envelope) {
        this.setStatus('[data-b3-email-status]', 'Check your email address.', { error: true });
        return;
      }
      this.state.busy = true;
      const button = this.$('[data-b3-send]');
      if (button) button.disabled = true;
      this.setStatus('[data-b3-email-status]', 'Sending…');
      try {
        const outcome = await postBigThree({ endpoint: this.submitEndpoint, envelope });
        if (!outcome.ok) {
          this.setStatus('[data-b3-email-status]', outcome.error === 'rate_limited'
            ? 'Please wait a minute before trying again.'
            : 'We could not add you just now. Please try again.', { error: true });
          return;
        }
        this.setStatus('[data-b3-email-status]', '');
        const success = this.$('[data-b3-success]');
        const testNote = this.$('[data-b3-dry-run-note]');
        if (testNote) testNote.hidden = outcome.mode !== 'dry-run';
        if (success) success.hidden = false;
        const emailForm = this.$('[data-b3-email-form]');
        if (emailForm) emailForm.hidden = true;
      } catch (error) {
        this.setStatus('[data-b3-email-status]', error?.message || 'Something went wrong. Please try again.', { error: true });
      } finally {
        this.state.busy = false;
        if (button) button.disabled = false;
      }
    }
  }

  customElements.define('starlyzed-big-three', StarlyzedBigThree);
}
